import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import EditProfileModal from './EditProfileModal';
import { collection, addDoc, query, where, getDocs, orderBy, deleteDoc, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { 
  getStoredAccessToken, 
  redirectToSpotifyAuth, 
  clearSpotifyAuth, 
  getAccessToken, 
  migrateSpotifyKeys,
  getMinutesUntilExpiry,
  isTokenExpired,
  isTokenExpiringSoon,
  isFirstTimeSpotifyUser,
  markFirstTimeTutorialShown
} from './spotifyAuth';
import PlaylistView from './PlaylistView';
import './Dashboard.css';

function Dashboard({ user }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);  
  const [shareEmail, setShareEmail] = useState('');  
  const [sharePermission, setSharePermission] = useState('edit');  
  const [sharing, setSharing] = useState(false); 
  const [selectedPlaylistForShare, setSelectedPlaylistForShare] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  
  // New state for token expiry warnings
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [expiryWarningType, setExpiryWarningType] = useState(null); // '5min', '1min', or 'expired'
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [remindMeLaterTime, setRemindMeLaterTime] = useState(null);
  const [showFirstTimeTutorial, setShowFirstTimeTutorial] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Set up real-time listener for playlists
  useEffect(() => {
    if (!user?.uid) return;
    
    setLoading(true);
    
    // Query 1: Get playlists where user is the owner
    const ownedQuery = query(
      collection(db, 'playlists'),
      where('ownerId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    
    // Query 2: Get playlists shared with this user
    const sharedQuery = query(
      collection(db, 'playlists'),
      where('sharedWith', 'array-contains', user.uid)
    );
    
    // Set up real-time listeners for both queries
    const unsubscribeOwned = onSnapshot(
      ownedQuery,
      (ownedSnapshot) => {
        const ownedPlaylists = [];
        ownedSnapshot.forEach((doc) => {
          ownedPlaylists.push({
            id: doc.id,
            ...doc.data(),
            isOwner: true
          });
        });
        
        // Now get shared playlists
        const unsubscribeShared = onSnapshot(
          sharedQuery,
          (sharedSnapshot) => {
            const sharedPlaylists = [];
            sharedSnapshot.forEach((doc) => {
              sharedPlaylists.push({
                id: doc.id,
                ...doc.data(),
                isOwner: false
              });
            });
            
            // Combine and sort
            const allPlaylists = [...ownedPlaylists, ...sharedPlaylists];
            allPlaylists.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            setPlaylists(allPlaylists);
            setLoading(false);
          },
          (error) => {
            console.error('Error listening to shared playlists:', error);
            setLoading(false);
          }
        );
        
        // Return cleanup for shared listener
        return unsubscribeShared;
      },
      (error) => {
        console.error('Error listening to owned playlists:', error);
        setLoading(false);
      }
    );
    
    // Cleanup both listeners on unmount
    return () => {
      unsubscribeOwned();
    };
  }, [user?.uid]);

  useEffect(() => {
    const handleSpotifyCallback = async () => {
      // Clean up any old/incorrect localStorage keys
      migrateSpotifyKeys();
      
      console.log('Dashboard mounted, checking Spotify status...');
      
      // Check if we're returning from Spotify authorization
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        console.log('Found authorization code, exchanging for token...');
        try {
          await getAccessToken(code);
          // Clear the code from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          setSpotifyConnected(true);
          
          // Show first-time tutorial if this is their first connection
          if (isFirstTimeSpotifyUser()) {
            setShowFirstTimeTutorial(true);
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
      } else {
        // No code, check if we have a valid token
        const token = await getStoredAccessToken();
        const hasToken = !!token;
        const expired = isTokenExpired();
        
        console.log('Token check:', { hasToken, expired });
        
        // Connected if we have a token AND it's not expired
        setSpotifyConnected(hasToken && !expired);
      }
    };
    
    handleSpotifyCallback();
  }, []);

  // Check token expiry every minute
  useEffect(() => {
    const checkTokenExpiry = () => {
      const minutesLeft = getMinutesUntilExpiry();
      
      console.log('Checking token expiry. Minutes left:', minutesLeft);
      
      if (minutesLeft === null) {
        // No token info available
        setSpotifyConnected(false);
        setShowExpiryWarning(false);
        return;
      }

      // Check if token is expired
      if (isTokenExpired()) {
        console.log('Token is EXPIRED');
        setExpiryWarningType('expired');
        setShowExpiryWarning(true);
        setSpotifyConnected(false);
        return;
      }

      // Token is valid, update connected state
      setSpotifyConnected(true);

      // Check if we should show 1-minute warning
      if (minutesLeft <= 1 && !warningDismissed) {
        console.log('Showing 1-minute warning');
        setExpiryWarningType('1min');
        setShowExpiryWarning(true);
        return;
      }

      // Check if "Remind Me Later" timer has expired
      if (remindMeLaterTime && Date.now() >= remindMeLaterTime) {
        console.log('Remind me later time expired, resetting');
        setRemindMeLaterTime(null);
        setWarningDismissed(false);
        // This will trigger the 1-minute warning on next check
      }

      // Check if we should show 5-minute warning
      if (isTokenExpiringSoon(5) && !warningDismissed && !remindMeLaterTime) {
        console.log('Showing 5-minute warning');
        setExpiryWarningType('5min');
        setShowExpiryWarning(true);
        return;
      }

      // Token is fine, hide warnings
      if (minutesLeft > 5) {
        setShowExpiryWarning(false);
        setWarningDismissed(false);
        setRemindMeLaterTime(null);
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Then check every 30 seconds
    const interval = setInterval(checkTokenExpiry, 30000);

    return () => clearInterval(interval);
  }, [warningDismissed, remindMeLaterTime]);

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim() || creating) return;

    setCreating(true);
    try {
      await addDoc(collection(db, 'playlists'), {
        name: newPlaylistName.trim(),
        ownerId: user.uid,
        songs: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sharedWith: [],
sharedWithDetails: {},  // ← Should be object, not array
      });
      
      setNewPlaylistName('');
      setShowCreateModal(false);
      // Real-time listener will auto-update
    } catch (error) {
      console.error('Error creating playlist:', error);
      alert('Failed to create playlist. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist? This cannot be undone.')) {
      return;
    }

    setDeletingId(playlistId);
    try {
      await deleteDoc(doc(db, 'playlists', playlistId));
      // Real-time listener will auto-update
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert('Failed to delete playlist. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const openShareModal = (playlist) => {
    setSelectedPlaylistForShare(playlist);
    setShowShareModal(true);
    setShareEmail('');
    setSharePermission('edit');
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setSelectedPlaylistForShare(null);
    setShareEmail('');
    setSharePermission('edit');
  };

  const handleSharePlaylist = async (e) => {
    e.preventDefault();
    if (!shareEmail.trim() || sharing) return;

    setSharing(true);
    try {
      // Find user by email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', shareEmail.trim().toLowerCase())
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        alert('No user found with that email address. Make sure they have created a Warrior Walkup account first.');
        setSharing(false);
        return;
      }

      const targetUser = usersSnapshot.docs[0];
      const targetUserId = targetUser.id;
      const targetUserData = targetUser.data();

      // Check if already shared
      if (selectedPlaylistForShare.sharedWith?.includes(targetUserId)) {
        alert('This playlist is already shared with that user.');
        setSharing(false);
        return;
      }

      // Add to sharedWith array and sharedWithDetails
      const playlistRef = doc(db, 'playlists', selectedPlaylistForShare.id);
      const updatedSharedWith = [...(selectedPlaylistForShare.sharedWith || []), targetUserId];
      const updatedSharedWithDetails = {
  ...(selectedPlaylistForShare.sharedWithDetails || {}),
  [targetUserId]: {
    email: shareEmail.trim().toLowerCase(),
    name: targetUserData.name || 'Unknown',
    permission: sharePermission,
    sharedAt: new Date().toISOString()
  }
};

      await updateDoc(playlistRef, {
        sharedWith: updatedSharedWith,
        sharedWithDetails: updatedSharedWithDetails
      });

      alert(`Playlist shared with ${shareEmail}!`);
      setShareEmail('');
      
      // Update local state
      setSelectedPlaylistForShare({
        ...selectedPlaylistForShare,
        sharedWith: updatedSharedWith,
        sharedWithDetails: updatedSharedWithDetails
      });
      
      // Real-time listener will auto-update
    } catch (error) {
      console.error('Error sharing playlist:', error);
      alert('Error sharing playlist: ' + error.message);
    } finally {
      setSharing(false);
    }
  };

  const handleRemoveAccess = (userId) => {
    // Find the user details
    const user = selectedPlaylistForShare.sharedWithDetails.find(u => u.userId === userId);
    if (user) {
      setUserToRemove(user);
      setShowRemoveConfirm(true);
    }
  };

  const confirmRemoveAccess = async () => {
    if (!userToRemove) return;
    
    try {
      const playlistRef = doc(db, 'playlists', selectedPlaylistForShare.id);
      
      const updatedSharedWith = selectedPlaylistForShare.sharedWith.filter(
        id => id !== userToRemove.userId
      );
      const updatedSharedWithDetails = selectedPlaylistForShare.sharedWithDetails.filter(
        detail => detail.userId !== userToRemove.userId
      );

      await updateDoc(playlistRef, {
        sharedWith: updatedSharedWith,
        sharedWithDetails: updatedSharedWithDetails
      });
      
      // Update local state
      setSelectedPlaylistForShare({
        ...selectedPlaylistForShare,
        sharedWith: updatedSharedWith,
        sharedWithDetails: updatedSharedWithDetails
      });
      
      // Real-time listener will auto-update
      
      // Close confirmation modal
      setShowRemoveConfirm(false);
      setUserToRemove(null);
      
    } catch (error) {
      console.error('Error removing access:', error);
      alert('Error removing access: ' + error.message);
      setShowRemoveConfirm(false);
      setUserToRemove(null);
    }
  };

  const handleChangePermission = async (userId, newPermission) => {
    try {
      const playlistRef = doc(db, 'playlists', selectedPlaylistForShare.id);
      
      const updatedSharedWithDetails = selectedPlaylistForShare.sharedWithDetails.map(detail =>
        detail.userId === userId
          ? { ...detail, permission: newPermission }
          : detail
      );

      await updateDoc(playlistRef, {
        sharedWithDetails: updatedSharedWithDetails
      });

      alert(`Permission changed to "${newPermission === 'edit' ? 'Can Edit' : 'View Only'}"!`);
      
      // Update local state
      setSelectedPlaylistForShare({
        ...selectedPlaylistForShare,
        sharedWithDetails: updatedSharedWithDetails
      });
      
      // Real-time listener will auto-update
    } catch (error) {
      console.error('Error changing permission:', error);
      alert('Error changing permission: ' + error.message);
    }
  };

  const handleSpotifyConnect = () => {
    redirectToSpotifyAuth();
  };

  const handleSpotifyDisconnect = () => {
    clearSpotifyAuth();
    setSpotifyConnected(false);
    setShowExpiryWarning(false);
  };

  const handleReconnectNow = () => {
    setShowExpiryWarning(false);
    setWarningDismissed(false);
    setRemindMeLaterTime(null);
    redirectToSpotifyAuth();
  };

  const handleRemindMeLater = () => {
    // Set reminder for 4 minutes from now (so it shows again at 1-minute mark)
    const fourMinutesFromNow = Date.now() + (4 * 60 * 1000);
    setRemindMeLaterTime(fourMinutesFromNow);
    setShowExpiryWarning(false);
  };

  const handleDismissWarning = () => {
    setWarningDismissed(true);
    setShowExpiryWarning(false);
  };

  const handleCloseTutorial = () => {
    setShowFirstTimeTutorial(false);
    markFirstTimeTutorialShown();
  };

  const handleLogout = async () => {
    try {
      // Tell Flutter to logout (if running in native app)
      if (window.FlutterChannel) {
        window.FlutterChannel.postMessage('logout');
      }
      
      // Logout from web
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
  };

  const closePlaylist = () => {
    setSelectedPlaylist(null);
    // Real-time listener keeps list updated
  };

  // Determine button style based on token status
  const getSpotifyButtonStyle = () => {
    if (!spotifyConnected || isTokenExpired()) {
      return {
        className: 'header-btn spotify-btn-reconnect',
        text: '🔄 Reconnect Spotify',
        color: '#DC3545' // Red
      };
    }
    
    if (isTokenExpiringSoon(5)) {
      return {
        className: 'header-btn spotify-btn-warning',
        text: '🔄 Reconnect Spotify',
        color: '#FFC107' // Orange
      };
    }
    
    return {
      className: 'header-btn spotify-connected',
      text: '🎵 Spotify Connected',
      color: '#1DB954' // Green
    };
  };

  const buttonStyle = getSpotifyButtonStyle();

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Warrior Walkup Music</h1>
            <p className="tagline">AI Powered Beats for Warrior Players</p>
          </div>
        </div>
        <div className="loading">Loading your playlists...</div>
      </div>
    );
  }

  if (selectedPlaylist) {
    return (
      <PlaylistView 
        playlist={selectedPlaylist} 
        onClose={closePlaylist}
        user={user}
      />
    );
  }

  return (
    <div className="dashboard-container">
      {/* Mobile Warning Banner */}
      {/Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.isNativeApp && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          padding: '15px',
          margin: '20px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#856404'
        }}>
          <strong>⚠️ Mobile Device Detected</strong>
          <p style={{ margin: '10px 0 0 0' }}>
            Warrior Walkup Music works best on desktop browsers (Chrome, Firefox, Edge). 
            Spotify playback is not supported on mobile browsers. Mobile app coming soon!
          </p>
        </div>
      )}
      
      {/* First-Time Tutorial Modal */}
      {showFirstTimeTutorial && (
        <div className="modal-overlay" onClick={handleCloseTutorial}>
          <div className="modal-content tutorial-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎵 Welcome to Spotify Integration!</h2>
            <div className="tutorial-content">
              <p><strong>Important to know:</strong></p>
              <p>Spotify requires all apps to refresh authentication every hour for security purposes. This is a Spotify policy, not a limitation of Warrior Walkup Music.</p>
              <p><strong>What this means for you:</strong></p>
              <ul>
                <li>You'll get a friendly reminder 5 minutes before your session expires</li>
                <li>Reconnecting takes just 10 seconds</li>
                <li>Perfect timing: reconnect during a hydration break!</li>
              </ul>
              <p style={{ fontSize: '14px', color: '#999', marginTop: '20px' }}>
                We'll guide you through it - no worries! 😊
              </p>
            </div>
            <button className="submit-btn" onClick={handleCloseTutorial}>
              Got it, let's go! 🚀
            </button>
          </div>
        </div>
      )}

      {/* Token Expiry Warning Banner */}
      {showExpiryWarning && expiryWarningType === '5min' && (
        <div className="expiry-warning-banner warning-5min">
          <div className="warning-content">
            <div className="warning-icon">🎵</div>
            <div className="warning-text">
              <strong>Spotify Timeout Alert!</strong>
              <p>Spotify requires a quick reconnection every hour (it's a Spotify thing, not us!). You have 5 minutes before your session expires. Click 'Reconnect Spotify' during a hydration break to keep your playlist game-ready. Takes just 10 seconds!</p>
            </div>
          </div>
          <div className="warning-actions">
            <button className="warning-btn primary" onClick={handleReconnectNow}>
              Reconnect Spotify Now
            </button>
            <button className="warning-btn secondary" onClick={handleRemindMeLater}>
              Remind Me Later
            </button>
          </div>
        </div>
      )}

      {showExpiryWarning && expiryWarningType === '1min' && (
        <div className="expiry-warning-banner warning-1min">
          <div className="warning-content">
            <div className="warning-icon">⏰</div>
            <div className="warning-text">
              <strong>Last Call!</strong>
              <p>Your Spotify session expires in 1 minute. Reconnect now to avoid interruption!</p>
            </div>
          </div>
          <div className="warning-actions">
            <button className="warning-btn primary" onClick={handleReconnectNow}>
              Reconnect Spotify Now
            </button>
            <button className="warning-btn secondary" onClick={handleDismissWarning}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showExpiryWarning && expiryWarningType === 'expired' && (
        <div className="expiry-warning-banner warning-expired">
          <div className="warning-content">
            <div className="warning-icon">⏰</div>
            <div className="warning-text">
              <strong>Time to Reconnect Spotify!</strong>
              <p>Spotify's security requires reconnection every hour. Don't worry - your playlist is safe! Just click below to get back in the game.</p>
            </div>
          </div>
          <div className="warning-actions">
            <button className="warning-btn primary" onClick={handleReconnectNow}>
              Reconnect to Spotify
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <div>
          <h1>Warrior Walkup Music</h1>
          <p className="tagline">AI Powered Beats for Warrior Players</p>
        </div>
        <div className="user-controls">
          <span className="user-email">{user.email}</span>
          {spotifyConnected && !isTokenExpired() ? (
            <div className="spotify-button-wrapper">
              <button 
                className={buttonStyle.className} 
                onClick={handleSpotifyDisconnect}
                style={{ backgroundColor: buttonStyle.color }}
              >
                {buttonStyle.text}
              </button>
              <button 
                className="info-tooltip-btn"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(!showTooltip)}
              >
                ℹ️
              </button>
              {showTooltip && (
                <div className="info-tooltip">
                  <strong>Why do I need to reconnect?</strong>
                  <p>Spotify requires all apps to refresh authentication every hour for security. This is a Spotify policy, not a limitation of Warrior Walkup Music.</p>
                  <p><strong>The good news:</strong> Reconnecting takes just 10 seconds and you can do it during a hydration break!</p>
                </div>
              )}
            </div>
          ) : (
            <button 
              className="header-btn spotify-btn" 
              onClick={handleSpotifyConnect}
              style={{ backgroundColor: '#DC3545' }}
            >
              🔄 Reconnect Spotify
            </button>
          )}
          <button className="header-btn" onClick={() => setShowEditProfile(true)}>
            Edit Profile
          </button>
          <button className="header-btn logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="playlists-header">
          <h2>My Playlists</h2>
          <button className="create-btn" onClick={() => setShowCreateModal(true)}>
            + Create Playlist
          </button>
        </div>

        {playlists.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎵</div>
            <h3>No playlists yet!</h3>
            <p>Create your first playlist to start managing walkup music for your team.</p>
            <button className="create-btn-large" onClick={() => setShowCreateModal(true)}>
              Create Your First Playlist
            </button>
          </div>
        ) : (
          <div className="playlists-grid">
            {playlists.map((playlist) => (
              <div key={playlist.id} className="playlist-card">
                <div className="playlist-header">
                  <h3>{playlist.name}</h3>
                  <div className="playlist-actions">
                    {playlist.isOwner && (
                      <>
                        <button 
                          className="share-btn" 
                          onClick={(e) => { e.stopPropagation(); openShareModal(playlist); }}
                          title="Share with teammates"
                        >
                          👥
                        </button>
                        <button 
                          className="delete-btn" 
                          onClick={(e) => { e.stopPropagation(); handleDeletePlaylist(playlist.id); }}
                          disabled={deletingId === playlist.id}
                          title="Delete your playlist"
                        >
                          {deletingId === playlist.id ? '...' : '🗑️'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="song-count">
                  {playlist.songs?.length || 0} songs
                  {!playlist.isOwner && (
                    <span className="view-only-badge">
                      {(() => {
  // Handle both array and object formats
  const details = playlist.sharedWithDetails;
  if (!details) return ' • Can Edit';
  
  if (Array.isArray(details)) {
    // Array format (old)
    const userDetail = details.find(d => d.userId === user.uid);
    return userDetail?.permission === 'view' ? ' • View Only' : ' • Can Edit';
  } else {
    // Object format (new)
    const userDetail = details[user.uid];
    return userDetail?.permission === 'view' ? ' • View Only' : ' • Can Edit';
  }
})()}
                    </span>
                  )}
                </div>
                <div className="playlist-footer">
                  <span className="playlist-date">
                    Created {new Date(playlist.createdAt).toLocaleDateString()}
                  </span>
                  <button className="open-btn" onClick={() => openPlaylist(playlist)}>
                    Open →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Playlist</h2>
            <p>Give your playlist a name to get started</p>
            
            <form onSubmit={handleCreatePlaylist}>
              <input
                type="text"
                placeholder="e.g., Spring Season 2025"
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                required
                autoFocus
              />
              <div className="modal-buttons">
                <button type="submit" className="submit-btn" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Playlist'}
                </button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShareModal && selectedPlaylistForShare && (
        <div className="modal-overlay">
          <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Share Playlist</h2>
            <p>Share "{selectedPlaylistForShare.name}" with another user</p>
            
            <form onSubmit={handleSharePlaylist}>
              <input
                type="email"
                placeholder="Enter user's email address"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required={selectedPlaylistForShare.sharedWithDetails?.length === 0}
                autoFocus
              />
              
              <div className="permission-selector">
                <label>
                  <input
                    type="radio"
                    value="edit"
                    checked={sharePermission === 'edit'}
                    onChange={(e) => setSharePermission(e.target.value)}
                  />
                  Can Edit (add/remove/reorder songs)
                </label>
                <label>
                  <input
                    type="radio"
                    value="view"
                    checked={sharePermission === 'view'}
                    onChange={(e) => setSharePermission(e.target.value)}
                  />
                  View Only (play songs only)
                </label>
              </div>

              {shareEmail.trim() && (
                <div className="modal-buttons">
                  <button type="submit" className="submit-btn" disabled={sharing}>
                    {sharing ? 'Sharing...' : 'Share Playlist'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={closeShareModal}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </form>

            {selectedPlaylistForShare.sharedWithDetails && Object.keys(selectedPlaylistForShare.sharedWithDetails).length > 0 && (
  <div className="shared-with-section">
    <h3>Currently Shared With:</h3>
    <div className="shared-users-list">
      {Object.entries(selectedPlaylistForShare.sharedWithDetails).map(([userId, detail]) => (
                    <div key={userId} className="shared-user-item">
                      <div className="shared-user-info">
                        <span className="shared-user-name">{detail.name}</span>
                        <span className="shared-user-email">{detail.email}</span>
                        <select
                          value={detail.permission}
                          onChange={(e) => handleChangePermission(userId, e.target.value)}
                          className="permission-dropdown"
                        >
                          <option value="edit">Can Edit</option>
                          <option value="view">View Only</option>
                        </select>
                      </div>
                      <button
                        className="remove-access-btn"
                        onClick={() => handleRemoveAccess(userId)}
                        title="Remove access"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Done button at bottom - always visible */}
            <div className="modal-buttons modal-buttons-bottom">
              <button 
                type="button" 
                className="submit-btn done-btn-bottom"
                onClick={closeShareModal}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showEditProfile && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEditProfile(false)}
          onSuccess={() => {}} // Real-time listener handles updates
        />
      )}

      {/* Support Contact Footer */}
      <div className="support-footer" style={{ paddingBottom: '8px' }}>
        <p>Contact Us: <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a></p>
      </div>
      
      {/* Remove User Confirmation Modal */}
      {showRemoveConfirm && userToRemove && (
        <div className="modal-overlay" onClick={() => setShowRemoveConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Remove User Access?</h2>
            <p style={{ textAlign: 'center', margin: '20px 0', color: '#666', fontSize: '15px' }}>
              Are you sure you want to remove <strong>{userToRemove.email}</strong> from this playlist?
              <br />
              They will no longer be able to access it.
            </p>
            <div className="modal-buttons">
              <button 
                className="delete-btn"
                onClick={confirmRemoveAccess}
              >
                Remove Access
              </button>
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setUserToRemove(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    <footer style={{
        textAlign: 'center',
        padding: '24px 20px',
        marginTop: '40px',
        borderTop: '1px solid #333',
        fontSize: '0.85rem',
        color: '#888'
      }}>
        <a href="https://warriorwalkupmusic.com/privacy" target="_blank" rel="noreferrer" style={{ color: '#888', marginRight: '20px', textDecoration: 'underline' }}>Privacy Policy</a>
        <a href="https://warriorwalkupmusic.com/terms" target="_blank" rel="noreferrer" style={{ color: '#888', textDecoration: 'underline' }}>Terms of Service</a>
        <p style={{ marginTop: '8px', fontSize: '0.8rem' }}>© 2026 Nerdette Ventures LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Dashboard;